import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, Contact, Download, Eye, FileText, Mail, MapPin, Pencil, Phone, Trash2,
} from 'lucide-react';
import { clientsApi, documentsApi, mattersApi, errorDetailText } from '../services/api';
import { Client } from '../types';
import { useClientsStore } from '../store/clientsStore';
import { useMattersStore } from '../store/mattersStore';
import { dayLabel, waLink } from '../components/practice/format';
import { Badge, Button, Skeleton, SkeletonCard } from '../components/ui';
import { WhatsAppIcon } from './landing/data';

const INPUT_CLS =
  'w-full text-sm border border-legal-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-accent';

/** Processing status, in words — same vocabulary as the All-documents library. */
function docStatus(status: string): { label: string; cls: string } {
  if (status === 'failed') return { label: "Couldn't read", cls: 'bg-red-50 text-red-700' };
  if (status === 'processing') return { label: 'Reading the text…', cls: 'bg-blue-50 text-blue-700' };
  return { label: 'Ready', cls: 'bg-green-50 text-green-700' };
}

/** One client: contact info, notes and every case of theirs. */
export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const setClientDetail = useClientsStore((s) => s.setClientDetail);
  const clearClient = useClientsStore((s) => s.clearClient);
  const fetchClients = useClientsStore((s) => s.fetchClients);
  const detail = useClientsStore((s) => (id ? s.detailById[id] : undefined));
  const listClient = useClientsStore((s) => (id ? s.clients.find((c) => c.id === id) : undefined));
  // Header-only seed: router state from the Clients list link, else the list cache.
  const routeSeed = (location.state as { client?: Client } | null)?.client;
  const header: Client | undefined =
    detail ?? (routeSeed?.id === id ? routeSeed : undefined) ?? listClient;

  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [edit, setEdit] = useState({
    name: '',
    phone: '',
    whatsapp: '',
    email: '',
    address: '',
    notes: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // ---- Documents from this client's cases (owner-only page; read-focused,
  // no upload here — uploads happen in chat / the case file). Derived from
  // each case's detail payload (the documents API doesn't carry matter_id),
  // cached in mattersStore so revisits are free.
  const detailById = useMattersStore((s) => s.detailById);
  const setMatterDetail = useMattersStore((s) => s.setMatterDetail);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [downloadUrls, setDownloadUrls] = useState<Record<string, string>>({});
  const caseIdsKey = detail?.cases.map((c) => c.id).join(',') ?? '';

  useEffect(() => {
    if (!detail || detail.cases.length === 0) return;
    let stale = false;
    setIsLoadingDocs(true);
    const missing = detail.cases.filter((c) => !useMattersStore.getState().detailById[c.id]);
    Promise.all([
      ...missing.map((c) =>
        mattersApi
          .get(c.id)
          .then(({ data }) => setMatterDetail(data))
          .catch(() => {}),
      ),
      // The library payload carries download_url (matter details don't).
      documentsApi
        .getDocuments()
        .then(({ data }) => {
          if (stale || !Array.isArray(data)) return;
          const map: Record<string, string> = {};
          data.forEach((d: { id: string; download_url?: string | null }) => {
            if (d.download_url) map[d.id] = d.download_url;
          });
          setDownloadUrls(map);
        })
        .catch(() => {}),
    ]).finally(() => {
      if (!stale) setIsLoadingDocs(false);
    });
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseIdsKey]);

  const clientDocs = useMemo(() => {
    if (!detail) return [];
    return detail.cases
      .flatMap((c) =>
        (detailById[c.id]?.documents ?? []).map((doc) => ({
          ...doc,
          caseId: c.id,
          caseTitle: c.title,
        })),
      )
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [detail, detailById]);

  // Always revalidate in the background; the seed/cache renders meanwhile.
  useEffect(() => {
    if (!id) return;
    let stale = false;
    Promise.all([clientsApi.get(id), clientsApi.cases(id)])
      .then(([clientRes, casesRes]) => {
        if (!stale) setClientDetail({ ...clientRes.data, cases: casesRes.data });
      })
      .catch((err: any) => {
        if (!stale) setError(errorDetailText(err, 'Failed to load client'));
      });
    return () => {
      stale = true;
    };
  }, [id, setClientDetail]);

  const startEdit = () => {
    if (!header) return;
    setEdit({
      name: header.name,
      phone: header.phone || '',
      whatsapp: header.whatsapp || '',
      email: header.email || '',
      address: header.address || '',
      notes: header.notes || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!id || !edit.name.trim() || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      const { data } = await clientsApi.update(id, {
        name: edit.name.trim(),
        phone: edit.phone.trim(),
        whatsapp: edit.whatsapp.trim(),
        email: edit.email.trim(),
        address: edit.address.trim(),
        notes: edit.notes.trim(),
      });
      if (detail) setClientDetail({ ...detail, ...data });
      setIsEditing(false);
      fetchClients();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not save changes.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (
      !confirm(
        'Remove this client from your book? Their cases are kept — they just lose the client link.'
      )
    )
      return;
    try {
      await clientsApi.remove(id);
      clearClient(id);
      fetchClients();
      navigate('/app/clients');
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not delete the client.'));
    }
  };

  if (!header && !error) {
    return (
      <div className="p-2 sm:p-6 max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!header) {
    return (
      <div className="p-2 sm:p-6 max-w-3xl mx-auto">
        <div className="flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-red-700">{error || 'Client not found'}</p>
        </div>
        <Link
          to="/app/clients"
          className="mt-4 inline-flex items-center space-x-2 text-legal-500 hover:text-legal-700"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Clients</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-6 max-w-3xl mx-auto">
      <button
        onClick={() => navigate('/app/clients')}
        className="flex items-center space-x-2 text-legal-500 hover:text-legal-700 mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm">Clients</span>
      </button>

      {/* Header / contact card */}
      <div className="bg-white rounded-card border border-legal-200 p-5 mb-6">
        {isEditing ? (
          <div className="space-y-3">
            <input
              value={edit.name}
              onChange={(e) => setEdit((f) => ({ ...f, name: e.target.value }))}
              maxLength={255}
              className={`${INPUT_CLS} text-lg font-semibold`}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="tel"
                value={edit.phone}
                onChange={(e) => setEdit((f) => ({ ...f, phone: e.target.value }))}
                placeholder="Phone"
                className={INPUT_CLS}
              />
              <input
                type="tel"
                value={edit.whatsapp}
                onChange={(e) => setEdit((f) => ({ ...f, whatsapp: e.target.value }))}
                placeholder="WhatsApp"
                className={INPUT_CLS}
              />
              <input
                type="email"
                value={edit.email}
                onChange={(e) => setEdit((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email"
                className={INPUT_CLS}
              />
              <input
                value={edit.address}
                onChange={(e) => setEdit((f) => ({ ...f, address: e.target.value }))}
                placeholder="Address"
                className={INPUT_CLS}
              />
            </div>
            <textarea
              value={edit.notes}
              onChange={(e) => setEdit((f) => ({ ...f, notes: e.target.value }))}
              maxLength={5000}
              rows={3}
              placeholder="Notes"
              className={`${INPUT_CLS} resize-none`}
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex items-center gap-2">
              <Button size="md" onClick={handleSave} isLoading={isSaving} disabled={!edit.name.trim()}>
                Save
              </Button>
              <Button variant="ghost" size="md" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="p-2.5 bg-legal-50 rounded-lg shrink-0">
                  <Contact className="h-5 w-5 text-legal-500" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-bold text-legal-900 break-words">{header.name}</h1>
                  <div className="text-sm text-legal-600 mt-1.5 space-y-1">
                    {header.phone && (
                      <p className="flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 text-legal-400" /> {header.phone}
                      </p>
                    )}
                    {header.email && (
                      <p className="flex items-center gap-1.5 break-all">
                        <Mail className="h-3.5 w-3.5 text-legal-400" /> {header.email}
                      </p>
                    )}
                    {header.address && (
                      <p className="flex items-start gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-legal-400 mt-0.5 shrink-0" />
                        <span className="break-words">{header.address}</span>
                      </p>
                    )}
                  </div>
                  {header.notes && (
                    <p className="text-sm text-legal-500 mt-2.5 whitespace-pre-wrap break-words">
                      {header.notes}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={startEdit}
                  className="p-2 text-legal-400 hover:text-legal-700 hover:bg-legal-50 rounded-lg transition-colors"
                  title="Edit client"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={handleDelete}
                  className="p-2 text-legal-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete client"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {header.phone && (
                <Button
                  variant="secondary"
                  size="md"
                  href={`tel:${header.phone}`}
                  data-track="call-client"
                >
                  <Phone className="h-4 w-4" /> Call
                </Button>
              )}
              {(header.whatsapp || header.phone) && (
                <Button
                  variant="secondary"
                  size="md"
                  href={waLink(header.whatsapp || header.phone || '')}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track="call-client"
                  className="text-[#25D366] hover:text-[#25D366]"
                >
                  <WhatsAppIcon className="h-4 w-4" /> WhatsApp
                </Button>
              )}
            </div>
            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
          </>
        )}
      </div>

      {/* Their cases */}
      <h2 className="font-semibold text-legal-900 mb-3">Cases</h2>
      {!detail ? (
        <SkeletonCard lines={2} />
      ) : detail.cases.length === 0 ? (
        <div className="bg-white rounded-card border border-legal-200 p-6 text-center text-sm text-legal-400">
          No cases linked to this client yet.
        </div>
      ) : (
        <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100 stagger-children">
          {detail.cases.map((c) => (
            <Link
              key={c.id}
              to={`/app/cases/${c.id}`}
              state={{ matter: c }}
              className="flex items-center justify-between gap-3 p-4 hover:bg-legal-50 transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-legal-900 truncate">{c.title}</p>
                <p className="text-xs text-legal-400 mt-0.5 truncate">
                  {[c.court_name, c.case_number].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {c.next_hearing_date && (
                  <span className="text-xs text-legal-500">{dayLabel(c.next_hearing_date)}</span>
                )}
                {c.case_stage && <Badge tone="info">{c.case_stage}</Badge>}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Documents from this client's cases (owner-only; read-focused — no upload here) */}
      <h2 className="font-semibold text-legal-900 mb-3 mt-6">Documents</h2>
      {isLoadingDocs && clientDocs.length === 0 ? (
        <SkeletonCard lines={2} />
      ) : clientDocs.length === 0 ? (
        <p className="text-sm text-legal-400">
          Documents from this client's cases will appear here.
        </p>
      ) : (
        <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100">
          {clientDocs.map((doc) => {
            const status = docStatus(doc.processing_status);
            return (
              <div key={doc.id} className="p-4">
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-legal-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-legal-900 truncate">
                      {doc.original_filename}
                    </p>
                    <p className="text-xs text-legal-400 mt-0.5 truncate">
                      <Link
                        to={`/app/cases/${doc.caseId}`}
                        data-track="client-document-case"
                        className="hover:text-accent-700 transition-colors"
                      >
                        {doc.caseTitle}
                      </Link>
                      {' · '}
                      {(doc.file_size_bytes / 1024).toFixed(0)} KB
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${status.cls}`}
                  >
                    {status.label}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-2 pl-7">
                  <Link
                    to={`/app/documents/${doc.id}`}
                    data-track="client-document-view"
                    className="inline-flex items-center gap-1 text-xs font-medium text-legal-500 hover:text-accent-700 transition-colors"
                  >
                    <Eye className="h-3.5 w-3.5" /> View
                  </Link>
                  {downloadUrls[doc.id] && (
                    <a
                      href={downloadUrls[doc.id]}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-track="client-document-download"
                      className="inline-flex items-center gap-1 text-xs font-medium text-legal-500 hover:text-accent-700 transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" /> Download
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
